import React from 'react';
import {
    annualizedOperatingData,
    getAnnualizedOperatingData,
    getLeaseSummaryData,
    getNetOperatingIncomeData,
    getOfferingSummaryData,
    getRentPricePercentageHeaders,
    getRentPricingData,
    leaseSummary,
    netOperatingIncome,
    offeringSummary,
    rentPricingData
} from "./tables";
import {addCommasToNumber} from "../../utils";


let cloneDeep = require('lodash.clonedeep');

const PRINT_MARKER_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 64">
  <path d="M24 2C14.06 2 6 10.06 6 20c0 13.5 15.3 30.8 17.2 32.9.4.45 1 .7 1.6.7.6 0 1.2-.25 1.6-.7C28.7 50.8 42 35.5 42 20 42 10.06 33.94 2 24 2z" fill="#142345"/>
  <polygon points="24,12 27.4,18.9 35,20 29.5,25.3 30.8,32.9 24,29.3 17.2,32.9 18.5,25.3 13,20 20.6,18.9" fill="#ffffff"/>
</svg>
`)}`;

export default class PrintSingleTenant extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: {
                offeringSummary: offeringSummary,
                leaseSummary: leaseSummary,
                annualizedOperatingData: annualizedOperatingData,
                netOperatingIncome: netOperatingIncome,
                rentPricingData
            }
        }
    }

    componentDidMount() {
        if (!document.querySelector('#mapboxstyles')) {
            const link = document.createElement('link');
            link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.0.0/mapbox-gl.css?optimize=true';
            link.rel = 'stylesheet';
            link.id = 'mapboxstyles';
            document.head.appendChild(link)
        }
        // Flag the body so the global CSS can hide the Kore phone SDK
        // floating iframe while the proposal print layout is on screen.
        if (typeof document !== 'undefined' && document.body) {
            document.body.classList.add('is-print-mode');
        }
        setTimeout(() => this.createMap(this.props.propertyMapProps), 1000);
    }

    componentWillUnmount() {
        if (typeof document !== 'undefined' && document.body) {
            document.body.classList.remove('is-print-mode');
        }
    }

    async componentWillReceiveProps(nextProps) {
        const data = cloneDeep(this.state.data);
        data.offeringSummary.rows =(this.props.isPropertyIndustrialVacant || this.props.isPropertyOfficeVacant || this.props.isPropertyRetailVacant ) ? getOfferingSummaryData(nextProps.tables).filter((value)=> !['Cap Rate','Net Operating Income'].includes(value.label)): getOfferingSummaryData(nextProps.tables).filter((value)=> value.label!=='Price PSF');
        data.leaseSummary.rows = getLeaseSummaryData(nextProps.tables);
        data.annualizedOperatingData.rows = getAnnualizedOperatingData(nextProps.tables);
        data.netOperatingIncome.value = getNetOperatingIncomeData(nextProps.tables);
        data.rentPricingData.rows = getRentPricingData(nextProps.tables)
        data.rentPricingData.percentageHeaders = getRentPricePercentageHeaders(nextProps.tables);

        this.setState({data});
    }


    static formatFirstLetterText(text) {
        return text;
    }

    // Renders the navy banner at the bottom of an STNL content page with the
    // Summit logo and, when available, up to two listing agents. A single
    // agent is centered in the banner; two agents are spaced apart. Mirrors
    // the MT `renderFooter` behavior (`.single` / `.multiple` modifiers) so
    // STNL and MT proposals keep a consistent look.
    static renderFooter(agents, className = 'PrintSingleTenant_footer') {
        const visibleAgents = (agents || []).filter(a => a && (a.name || a.phone || a.email));
        const hasAgents = visibleAgents.length > 0;
        const modifier = visibleAgents.length >= 2 ? 'multiple' : 'single';
        return (
            <div className={className}>
                {hasAgents && (
                    <div className={`${className}_agents ${modifier}`}>
                        {visibleAgents.slice(0, 2).map((agent, idx) => {
                            const contactParts = [];
                            if (agent.phone) contactParts.push(agent.phone);
                            if (agent.email) contactParts.push(agent.email);
                            return (
                                <div key={idx} className={`${className}_agent`}>
                                    <span className={`${className}_agent_name`}>{agent.name}</span>
                                    {contactParts.length > 0 && (
                                        <span className={`${className}_agent_contact`}>
                                            {contactParts.map((part, i) => (
                                                <React.Fragment key={i}>
                                                    {i > 0 && <span className={`${className}_agent_sep`}>|</span>}
                                                    <span>{part}</span>
                                                </React.Fragment>
                                            ))}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className={`${className}_logo`}>
                    <img src='/img/inline-light-logo.png' alt=''/>
                </div>
            </div>
        );
    }
    


    static renderTableMatrix(table) {
        const title = PrintSingleTenant.formatFirstLetterText(table.name);
        // Long annualized-operating-data tables (e.g. 15-year leases with
        // multiple option chains) can crowd the NOI strip toward the footer.
        // Tag the matrix with `compact` / `compact-tight` so SCSS tightens
        // this table only — never truncate rows in render().
        const rowCount = (table.rows && table.rows.length) || 0;
        let compactClass = '';
        if (rowCount > 14) {
            compactClass = 'compact compact-tight';
        } else if (rowCount > 10) {
            compactClass = 'compact';
        }
        return (
            <div className={`PrintSingleTenant_content_table PrintSingleTenant_content_table--annualized ${compactClass}`.trim()}>
                <div className='PrintSingleTenant_content_table_title'>{title}</div>
                {table.header ?
                    <div className='PrintSingleTenant_content_table_row is-header'>
                        {table.header.map(cell => {
                            return (
                                <div key={cell} className='PrintSingleTenant_content_table_row_cell'>{cell}</div>
                            )
                        })}
                    </div>
                    : null
                }
                {table.rows.map((row, index) => {
                    return (
                        <div key={index} className={`PrintSingleTenant_content_table_row ${index % 2 !== 0 ? '' : 'white'}`}>
                            {row.valueCells.map((cell, cellIndex) => {
                                return (
                                    <div key={`${index}-${cellIndex}`} className='PrintSingleTenant_content_table_row_cell'>{cell.value}</div>
                                )
                            })}
                        </div>
                    )
                })}
            </div>
        )
    }


    static renderPriceRentMatrix(table) {
        return (
            <div className='PrintSingleTenant_content_table'>
                {table.percentageHeaders ?
                    <div className='PrintSingleTenant_content_table_headerrow_percentages'>
                        {table.percentageHeaders.map(cell => {
                            return (
                                <div key={cell} className='PrintSingleTenant_content_table_title percentage-rent-header'>{cell}</div>
                            )
                        })}
                    </div>
                    : null
                }
                {table.header ?
                    <div className='PrintSingleTenant_content_table_headerrow'>
                        {table.header.map(cell => {
                            return (
                                <div key={cell} className='PrintSingleTenant_content_table_title price-rent-header'>{cell}</div>
                            )
                        })}
                    </div>
                    : null
                }
                {table.rows.map((row, index) => {
                    // The middle row (index 3) renders WHITE regardless, so we keep
                    // straight index-parity alternation: even -> gray (.white class),
                    // odd -> default white. Because index 3 is odd, the middle row
                    // sits naturally on a "white" slot and alternation continues
                    // cleanly on either side without doubling up.
                    let rowClass;
                    if (index === 3) {
                        rowClass = 'middlerow';
                    } else {
                        rowClass = index % 2 === 0 ? 'white' : '';
                    }
                    return (
                        <div key={index} className={`PrintSingleTenant_content_table_matrix_row ${rowClass}`}>
                            {row.valueCells.map((cell, cellIndex) => {
                                return (
                                    <div key={`${index}-${cellIndex}`} className='PrintSingleTenant_content_table_matrix_row_matrixcell'>{cell.value}</div>
                                )
                            })}
                        </div>
                    )
                })}
            </div>
        )
    }

    static renderTable(table) {
        let title = PrintSingleTenant.formatFirstLetterText(table.name);
        const isNoiTable = typeof table.name === 'string' && table.name.toUpperCase() === 'NET OPERATING INCOME';
        const isLeaseSummary = typeof table.name === 'string' && table.name.toUpperCase() === 'LEASE SUMMARY';
        return (
            <div className={`PrintSingleTenant_content_table ${table.value ? 'no-margin' : ''} ${isNoiTable ? 'PrintSingleTenant_content_table--noi' : ''} ${isLeaseSummary ? 'PrintSingleTenant_content_table--lease-summary' : ''}`.trim()}>
                {table.value ?
                    <div className='PrintSingleTenant_content_table_one_row'>
                        <div className='PrintSingleTenant_content_table_title'>{title}</div>
                        <div className='PrintSingleTenant_content_table_value'>{table.value}</div>
                    </div>
                    :
                    <div className='PrintSingleTenant_content_table_title'>{title}</div>
                }
                {table.rows ?
                    table.rows.map((row, index) => {
                        return (
                            <div key={index} className={`PrintSingleTenant_content_table_column ${index % 2 !== 0 ? 'white' : ''} ${table.rows.length - 1 === index && index % 2 === 0 ? 'last-row' : ''}`}>
                                <div>{row.label}</div>
                                <div>{row.value}</div>
                            </div>
                        )
                    })
                    : null
                }
            </div>
        );
    }

    getCapValue (index,isPsf) {
        const rowType = isPsf ? 'sale-price-psf':'cap-rate';
        return this.props.tables['pricing-matrix'].rows[rowType].valueCells[index].getComputedValue(this.props.tables).displayValue || '0';
    }

    async createMap(propertyMapProps) {
        let {default: mapboxgl} = await import('mapbox-gl/dist/mapbox-gl.js');

        mapboxgl.accessToken = 'pk.eyJ1IjoiYW5kcmVlYW1pcmNlYSIsImEiOiJja2lpa25rNzQwYWV0MnNxcGdscDVnbGc3In0.YW3ma8Gr7xlYFsAfLwTL1A';
        let map = new mapboxgl.Map({
            container: 'map',
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [propertyMapProps.LONGITUDE, propertyMapProps.LATITUDE],
            zoom: 12,
            minzoom: 12,
            maxzoom: 12,
        });

        let el = document.createElement('div');
        el.className = 'marker';
        el.style.backgroundImage = `url("${PRINT_MARKER_SVG}")`;
        el.style.backgroundSize = '30px 45px';
        el.style.backgroundRepeat = 'no-repeat';
        el.style.width = '30px';
        el.style.height = '45px';

        new mapboxgl.Marker(el).setLngLat([propertyMapProps.LONGITUDE, propertyMapProps.LATITUDE])
            .addTo(map);

        const propertyName = propertyMapProps.NAME;
        new mapboxgl.Popup()
            .setLngLat([propertyMapProps.LONGITUDE, propertyMapProps.LATITUDE])
            .setHTML(propertyName)
            .addTo(map);
    }

    getCoverImage() {
        const { isCategoryOffice, isPropertyIndustrial, isPropertyIndustrialVacant } = this.props;
        if (isCategoryOffice) {
            return '/img/Office-STNL-and-office-MT-page-1.jpeg';
        }
        return '/img/Retail-STNL-page-1.jpeg';
    }

    render() {
        const { isPropertyIndustrialVacant, isCategoryOffice ,isPropertyOfficeVacant,isPropertyRetailVacant, isPropertyIndustrial, isSaleLeaseBack} = this.props;
        const isPricingAnalysisMarketingProposal = isPropertyIndustrialVacant || isPropertyOfficeVacant || isPropertyRetailVacant ;
        const vacantProperty = isPropertyIndustrialVacant || isPropertyOfficeVacant || isPropertyRetailVacant;
        const data = this.state.data;
        const name = this.props.tables['general-information'].rows['property-name'].value;
        const address = this.props.tables['general-information'].rows['address'].value;
        const city = this.props.tables['general-information'].rows['city'].value;
        const state = this.props.tables['general-information'].rows['state'].value;
        const zip = this.props.tables['general-information'].rows['zip'].value;
        const photo = arc.path.media + this.props.propertyImage;
        const agents = this.props.agents;
        const aggressive = this.props.tables['pricing-matrix'].rows['value'].valueCells[1].getComputedValue( this.props.tables).internalValue || '0';
        const anticipated = this.props.tables['pricing-matrix'].rows['value'].valueCells[2].getComputedValue( this.props.tables).internalValue || '0';
        const conservative = this.props.tables['pricing-matrix'].rows['value'].valueCells[3].getComputedValue( this.props.tables).internalValue || '0';
        const aggressive_cap = this.getCapValue(1, isPricingAnalysisMarketingProposal);
        const anticipated_cap = this.getCapValue(2, isPricingAnalysisMarketingProposal);
        const conservative_cap = this.getCapValue(3, isPricingAnalysisMarketingProposal);
        const yearBuilt = this.props.tables['offering-summary'].rows['year-built'].value || '-';
        const noi = this.props.tables['offering-summary'].rows['net-operating-income'].getComputedValue( this.props.tables).displayValue || '-';
        const lot_size = this.props.tables['offering-summary'].rows['lot-size'].value || '0';
        const Strengths = this.props.proposalData.STRENGTHS || '';
        const challenges = this.props.proposalData.CHALLENGES || '';
        const buyerPool = this.props.proposalData.BUYER_POOL || '';
        const qualifications = this.props.proposalData.QUALIFICATIONS || '';
        const recommendations = this.props.proposalData.RECOMMENDATIONS || '';
        const lease_types = this.props.tables['lease-summary'].rows['lease-type'].value || '-';
        const isLeaseTypeGround = this.props.tables['lease-summary'].rows['lease-type'].value == "Ground";
        const gla = this.props.tables['offering-summary'].rows['gross-leasable-area'].value || '-';
        const coverImage = this.getCoverImage();
        const hasSummaryPropertyImage = Boolean(
            this.props.propertyImage && !isPricingAnalysisMarketingProposal && !isSaleLeaseBack
        );
        const annualizedRowCount = (data.annualizedOperatingData.rows && data.annualizedOperatingData.rows.length) || 0;
        const summaryPageRowClass = [
            'horizontal second',
            hasSummaryPropertyImage ? 'align-summary-panels' : '',
            annualizedRowCount > 14 ? 'compact-annualized-tight' : '',
        ].filter(Boolean).join(' ');

        return (
            <div className='SingleTenant'>
                {(this.props.isCategoryRetail || isPropertyIndustrialVacant || isCategoryOffice || isPropertyIndustrial) ? <div className='content cover-page' style={{ position: 'relative', padding: 0 }}>
                    {/* 782px = 810px page height minus the 2x14px white frame border
                        (PrintSingleTenant.scss), so the cover sheet totals exactly one
                        page and never spills a sliver onto an extra blank sheet. */}
                    <img src={coverImage} style={{ width: '100%', height: '782px', objectFit: 'contain', display: 'block' }} />
                    <div className='bottom-section' style={{ position: 'absolute', left: "5%", bottom: "3%", maxWidth: '80%' }}>
                        <div className='bs-line bs-line-1'>Pricing Analysis & Marketing Proposal</div>
                        <div className='bs-line bs-line-2' title={name}>{name}</div>
                        <div className='bs-line bs-line-3'>{`${city}, ${state}`}</div>
                    </div>
                </div> : null}
            <div className='PrintSingleTenant'>
                <div className='PrintSingleTenant_property_label'>{name}</div>
                <div className="parent">
                    <div className="horizontal first">
                        <div className='PrintSingleTenant_content_name'>{name}</div>
                        <div className='PrintSingleTenant_content_address'>{address}</div>
                        <div className='PrintSingleTenant_content_address'>{`${city}, ${state} ${zip}`}</div>
                    </div>
                    <div className={summaryPageRowClass}>
                        <div className="vertical left">
                            {PrintSingleTenant.renderTable(data.offeringSummary)}
                            {!isPricingAnalysisMarketingProposal && PrintSingleTenant.renderTable(data.leaseSummary)}
                        
                        </div>
                        <div className="vertical right">
                        {

                            this.props.propertyImage && !isPricingAnalysisMarketingProposal && !isSaleLeaseBack &&
                            <div className='PrintSingleTenant_content_image'>
                                {photo && <img src={photo} alt=''/>}
                            </div>  
                        }
                        {
                            (isPricingAnalysisMarketingProposal || isSaleLeaseBack) &&
                            <div id='map'/>
                        }
                        {!isPricingAnalysisMarketingProposal && PrintSingleTenant.renderTableMatrix(data.annualizedOperatingData)}
                        {!isPricingAnalysisMarketingProposal && PrintSingleTenant.renderTable(data.netOperatingIncome)}
                        </div>
                    </div>
                </div>
                {
                isPricingAnalysisMarketingProposal &&
                    <div className='PrintSingleTenant_coverimage'>
                       {photo && <img src={photo} alt=''/>}
                    </div>
                }
                {PrintSingleTenant.renderFooter(agents)}
            </div>
                {
                    isSaleLeaseBack &&
                    (
                        <div className='PrintSingleTenant'>
                            <div className='PrintSingleTenant_property_label'>{name}</div>
                            <div className="parent">
                                <div className="horizontal first">
                                    <h2 style={{ color: "#2B3A67" }}>{PrintSingleTenant.formatFirstLetterText("RENT & PRICING RANGE", "32px")}</h2>
                                </div>
                                <div className="horizontal first">
                                    <div className="vertical-full" style={{width:"90%",margin:"0 auto"}}>
                                        {PrintSingleTenant.renderPriceRentMatrix(data.rentPricingData)}
                                    </div>
                                </div>
                            </div>
                            <div className='PrintSingleTenant_coverimage rent-pricing'>
                                {photo && <img src={photo} alt='' />}
                            </div>
                            {PrintSingleTenant.renderFooter(agents)}
                        </div>
                    )
                }
            {(this.props.isCategoryRetail || isPropertyIndustrialVacant || isCategoryOffice || isPropertyIndustrial) ?
            <div className="va-page">
                <div className="va-header">
                    <div className="va-property-label">{name}</div>
                </div>
                <div className="va-top">
                    <div className="va-title">Value Analysis</div>
                </div>
                <div className="va-curve-section">
                    <img src="/img/va-curve.JPG" alt="" />
                </div>
                <div className="va-pricing">
                    <div className="va-price-item">
                        <div className="va-price-label">Conservative</div>
                        <div className="va-price-value">${addCommasToNumber(conservative)}</div>
                        <div className="va-price-cap">{conservative_cap} {isPricingAnalysisMarketingProposal ? 'PSF':'Cap'}</div>
                    </div>
                    <div className="va-price-item">
                        <div className="va-price-label">Anticipated</div>
                        <div className="va-price-value">${addCommasToNumber(anticipated)}</div>
                        <div className="va-price-cap">{anticipated_cap} {isPricingAnalysisMarketingProposal ? 'PSF':'Cap'}</div>
                    </div>
                    <div className="va-price-item">
                        <div className="va-price-label">Aggressive</div>
                        <div className="va-price-value">${addCommasToNumber(aggressive)}</div>
                        <div className="va-price-cap">{aggressive_cap} {isPricingAnalysisMarketingProposal ? 'PSF':'Cap'}</div>
                    </div>
                </div>
                <div className="va-data-strip">
                    <div className="va-data-item">
                        <img src="/img/va-icon-calendar.JPG" alt="" />
                        <div className="va-data-label">Year Built</div>
                        <div className="va-data-value">{yearBuilt}</div>
                    </div>
                    <div className="va-data-item">
                        <img src="/img/va-icon-dollar.JPG" alt="" />
                        <div className="va-data-label">NOI</div>
                        <div className="va-data-value">{addCommasToNumber(noi)}</div>
                    </div>
                    {!vacantProperty ?
                        <div className="va-data-item">
                            <img src="/img/va-icon-clipboard.JPG" alt="" />
                            <div className="va-data-label">Lease Type</div>
                            <div className="va-data-value">{isLeaseTypeGround ? "Ground Lease" : lease_types}</div>
                        </div>
                    :
                        <div className="va-data-item">
                            <img src="/img/va-icon-clipboard.JPG" alt="" />
                            <div className="va-data-label">GLA</div>
                            <div className="va-data-value">{addCommasToNumber(gla)} SF</div>
                        </div>
                    }
                    <div className="va-data-item">
                        <img src="/img/va-icon-arrow.JPG" alt="" />
                        <div className="va-data-label">Lot Size</div>
                        <div className="va-data-value">{lot_size} Acres</div>
                    </div>
                </div>
                <div className="va-bottom">
                    <div className="va-col">
                        <div className="va-section">
                            <div className="va-section-header">
                                <img src="/img/va-icon-strengths.JPG" alt="" />
                                <div className="va-section-title">Strengths</div>
                            </div>
                            <ul>{Strengths.split('\n').splice(0, 3).map((res, i) => <li key={i}>{res}</li>)}</ul>
                        </div>
                        <div className="va-section">
                            <div className="va-section-header">
                                <img src="/img/va-icon-challenges.JPG" alt="" />
                                <div className="va-section-title">Challenges</div>
                            </div>
                            <ul>{challenges.split('\n').splice(0, 3).map((res, i) => <li key={i}>{res}</li>)}</ul>
                        </div>
                        <div className="va-section">
                            <div className="va-section-header">
                                <img src="/img/va-icon-buyerpool.JPG" alt="" />
                                <div className="va-section-title">Buyer Pool</div>
                            </div>
                            <ul>{buyerPool.split('\n').splice(0, 3).map((res, i) => <li key={i}>{res}</li>)}</ul>
                        </div>
                    </div>
                    <div className="va-col">
                        <div className="va-section">
                            <div className="va-section-header">
                                <img src="/img/va-icon-qualifications.JPG" alt="" />
                                <div className="va-section-title">Qualifications</div>
                            </div>
                            <ul>{qualifications.split('\n').splice(0, 5).map((res, i) => <li key={i}>{res}</li>)}</ul>
                        </div>
                        <div className="va-section">
                            <div className="va-section-header">
                                <img src="/img/va-icon-recommendations.JPG" alt="" />
                                <div className="va-section-title">Recommendations & Timing</div>
                            </div>
                            <ul>{recommendations.split('\n').splice(0, 2).map((res, i) => <li key={i}>{res}</li>)}</ul>
                        </div>
                    </div>
                </div>
            </div>
            : null}
            {
                    this.props.staticImages.map((img, index) => {
                        return (this.props.isCategoryRetail || isPropertyIndustrialVacant || isCategoryOffice || isPropertyIndustrial) ? (<React.Fragment key={index}>
                        <div className="PrintSingleTenant" style={{ height: 'auto', padding: 0 }}>
                                <div className='content' style={{ position: 'relative', padding: 0 }}>
                                    <img className='staticImg' src={`${img}`} />
                                </div>
                            </div>
                        </React.Fragment>) : null
                    })
                }
            </div>
        )

        
    }
}
